/**
 * Cookie consent context: state, persistence, and script application.
 * When consent changes, tracking scripts are applied immediately.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getConsent, hasUserConsented, setConsent } from './storage';
import { applyConsentToScripts } from './scriptLoader';
import type { CookieConsent } from './types';
import { ConsentContext, type ConsentContextValue } from './consentContextValue';

function readInitialConsent(): CookieConsent | null {
  return getConsent();
}

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [restoredConsent] = useState<CookieConsent | null>(readInitialConsent);
  const [consent, setConsentState] = useState<CookieConsent | null>(restoredConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(!hasUserConsented());

  const setConsentAndApply = useCallback((next: Partial<CookieConsent>) => {
    const full = setConsent(next);
    setConsentState(full);
    applyConsentToScripts(full);
  }, []);

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
    setBannerOpen(false);
  }, []);

  const openBanner = useCallback(() => {
    setBannerOpen(true);
    setPreferencesOpen(false);
  }, []);

  // Apply scripts when consent state is restored (e.g. on load)
  useEffect(() => {
    if (restoredConsent) applyConsentToScripts(restoredConsent);
  }, [restoredConsent]);

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent,
      hasConsented: hasUserConsented(),
      setConsentAndApply,
      openPreferences,
      openBanner,
      preferencesOpen,
      setPreferencesOpen,
      bannerOpen,
      setBannerOpen,
    }),
    [
      consent,
      setConsentAndApply,
      openPreferences,
      openBanner,
      preferencesOpen,
      bannerOpen,
    ]
  );

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
}
